/**
 * Spotify Search Service
 * Handles artist search using live Spotify Web API
 * Requires valid authentication token
 */

import { Artist } from '../types/MusicPlatform';
import { searchLogger } from '../utils/SearchLogger';

interface SpotifyArtist {
  id: string;
  name: string;
  external_urls: { spotify: string };
  images: Array<{ url: string; height: number; width: number; }>;
  followers: { total: number };
  genres: string[];
  popularity: number;
}

export class SpotifySearch {
  private static instance: SpotifySearch;
  private baseUrl = 'https://api.spotify.com/v1';

  private constructor() {}

  static getInstance(): SpotifySearch {
    if (!SpotifySearch.instance) {
      SpotifySearch.instance = new SpotifySearch();
    }
    return SpotifySearch.instance;
  }

  /**
   * Search for artists on Spotify
   */
  async search(query: string): Promise<Artist[]> {
    const searchId = searchLogger.startSearch(query);
    
    const token = this.getSpotifyToken();
    if (!token) {
      searchLogger.api(searchId, 'spotify', 'error', { error: 'No authentication token available' });
      searchLogger.endSearch(searchId, 0, 'spotify-auth-error');
      return [];
    }
    
    try {
      searchLogger.api(searchId, 'spotify', 'start');
      const apiResults = await this.searchSpotifyAPI(query, token);
      searchLogger.api(searchId, 'spotify', 'success', { count: apiResults.length });
      searchLogger.endSearch(searchId, apiResults.length, 'spotify');
      return apiResults;
    } catch (error) {
      searchLogger.api(searchId, 'spotify', 'error', { error: error instanceof Error ? error.message : 'Unknown error' });
      searchLogger.endSearch(searchId, 0, 'spotify-error');
      return [];
    }
  }

  /**
   * Search using real Spotify API
   */
  private async searchSpotifyAPI(query: string, token: string): Promise<Artist[]> {
    const response = await fetch(
      `${this.baseUrl}/search?q=${encodeURIComponent(query)}&type=artist&limit=10`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Spotify API error: ${response.status}`);
    }

    const data = await response.json();
    return data.artists.items.map((artist: SpotifyArtist) => this.transformSpotifyArtist(artist));
  }


  /**
   * Transform Spotify API artist to unified Artist interface
   */
  private transformSpotifyArtist(spotifyArtist: SpotifyArtist): Artist {
    return {
      name: spotifyArtist.name,
      sortName: spotifyArtist.name,
      verified: true,
      source: 'spotify',
      platform: 'spotify',
      externalUrl: spotifyArtist.external_urls.spotify,
      followers: spotifyArtist.followers.total,
      genres: spotifyArtist.genres,
      artwork: this.getBestArtistImage(spotifyArtist.images)
    };
  }

  /**
   * Select the best artist image size for face visibility
   * Prefer medium-sized images (around 300-600px) over very large ones
   */
  private getBestArtistImage(images?: Array<{url: string, width: number, height: number}>): {url: string, width: number, height: number} | undefined {
    if (!images || images.length === 0) return undefined;
    
    // Sort images by width, prefer medium sizes (300-600px) for better face crops
    const sortedImages = images.sort((a, b) => {
      const aScore = this.getImageScore(a.width);
      const bScore = this.getImageScore(b.width);
      return bScore - aScore; // Higher score is better
    });
    
    return {
      url: sortedImages[0].url,
      width: sortedImages[0].width,
      height: sortedImages[0].height
    };
  }

  /**
   * Score images based on size - prefer medium sizes for better face crops
   */
  private getImageScore(width: number): number {
    if (width >= 300 && width <= 600) return 100; // Perfect size
    if (width >= 200 && width < 300) return 80;   // Good smaller size
    if (width > 600 && width <= 1000) return 60;  // Acceptable larger size
    if (width > 1000) return 20;                  // Too large, likely to crop faces
    return 10; // Too small
  }

  /**
   * Get Spotify access token from the main platform service
   */
  private getSpotifyToken(): string | null {
    try {
      // First try to get token from the platform factory
      const { musicPlatformFactory } = require('./MusicPlatformFactory');
      const currentService = musicPlatformFactory.getCurrentService();
      
      if (currentService && currentService.platformType === 'spotify') {
        const token = currentService.getToken();
        if (token) {
          console.log('🟢 Got Spotify token from platform service');
          return token;
        }
      }
      
      // Fallback to localStorage method
      const tokenData = localStorage.getItem('spotify_tokens');
      if (!tokenData) return null;
      
      const tokens = JSON.parse(tokenData);
      
      // Check if token is expired
      if (tokens.expires_at <= Date.now()) {
        return null;
      }
      
      console.log('🟢 Got Spotify token from localStorage');
      return tokens.access_token;
    } catch (error) {
      console.error('🟢 Error getting Spotify token:', error);
      return null;
    }
  }
}

export default SpotifySearch;